import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Upload } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { api, PROJECT_STATUSES, TASK_STATUSES, PRIORITIES, PHASES, DOC_CATEGORIES } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Progress } from "../ui/progress";
import { MemberItems, ORGS } from "./MemberSelect";

const F = ({ label, children, full }) => (
  <div className={full ? "col-span-2" : "col-span-2 sm:col-span-1"}>
    <Label className="mb-1.5 block text-xs font-medium text-slate-600">{label}</Label>
    {children}
  </div>
);

const Sel = ({ value, onChange, options, placeholder, testid, allowNone, team }) => (
  <Select value={value || (allowNone ? "none" : undefined)} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
    <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      {allowNone && <SelectItem value="none">{allowNone}</SelectItem>}
      {team ? <MemberItems team={team} /> : options.map((o) => (
        <SelectItem key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const DEFAULTS = {
  project: { name: "", status: "Not Started", priority: "Medium", customerId: "1", pmId: "", startDate: "", dueDate: "", description: "" },
  task: { title: "", projectId: "", status: "Not Started", priority: "Medium", phase: "Planning", assigneeId: "", startDate: "", dueDate: "", description: "" },
  customer: { name: "", contact: "", title: "", email: "", phone: "", web: "", street1: "", city: "", postcode: "", country: "United Kingdom", notes: "" },
  team: { name: "", role: "", org: "EP Group", email: "", phone: "", mobile: "" },
  document: { name: "", description: "", category: "Requirements", projectId: "", url: "" },
};
const TITLES = { project: "Project", task: "Task", customer: "Customer", team: "Team Member", document: "Document" };

export default function Dialogs() {
  const { dialog, closeDialog, projects, team, customers, user, refresh, openTask } = useApp();
  const nav = useNavigate();
  const [f, setF] = useState({});
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const type = dialog?.type;
  const editing = !!dialog?.initial?.id;

  useEffect(() => {
    if (dialog) {
      setF({ ...DEFAULTS[dialog.type], ...dialog.initial });
      setFile(null);
      setPct(0);
    }
  }, [dialog]);

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));
  const pOpts = projects.map((p) => ({ value: p.id, label: p.name }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (type === "project") {
        if (!f.name?.trim()) throw new Error("Project name is required");
        const body = { ...f, pmId: f.pmId || null, actorId: user.id };
        const r = editing ? await api.put(`/projects/${f.id}`, body) : await api.post("/projects", body);
        toast.success(editing ? "Project updated" : "Project created");
        if (!editing) nav(`/projects/${r.data.id}`);
      } else if (type === "task") {
        if (!f.title?.trim() || !f.projectId) throw new Error("Task name and project are required");
        const r = await api.post("/tasks", { ...f, assigneeId: f.assigneeId || null, actorId: user.id });
        toast.success("Task created");
        await refresh();
        openTask(r.data.id);
      } else if (type === "customer") {
        if (!f.name?.trim()) throw new Error("Customer name is required");
        editing ? await api.put(`/customers/${f.id}`, f) : await api.post("/customers", f);
        toast.success(editing ? "Customer updated" : "Customer added");
      } else if (type === "team") {
        if (!f.name?.trim()) throw new Error("Name is required");
        editing ? await api.put(`/team/${f.id}`, f) : await api.post("/team", f);
        toast.success(editing ? "Team member updated" : "Team member added");
      } else if (type === "document") {
        if (!f.name?.trim()) throw new Error("Document name is required");
        const fd = new FormData();
        ["name", "description", "category", "projectId", "url"].forEach((k) => fd.append(k, f[k] || ""));
        fd.append("uploadedBy", user.id);
        if (file) fd.append("file", file);
        await api.post("/documents", fd, { onUploadProgress: (ev) => ev.total && setPct(Math.round((ev.loaded / ev.total) * 100)) });
        toast.success("Document added");
      }
      refresh();
      closeDialog();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!dialog} onOpenChange={(o) => !o && closeDialog()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid={`dialog-${type}`}>
        <DialogHeader>
          <DialogTitle>{editing ? "Edit" : "Add a New"} {TITLES[type]}</DialogTitle>
          <DialogDescription>{editing ? "Update the details below." : `Create a new ${TITLES[type]?.toLowerCase()} for the Harrods programme.`}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          {type === "project" && (
            <>
              <F label="Project name *" full><Input data-testid="project-name-input" value={f.name || ""} onChange={set("name")} autoFocus /></F>
              <F label="Status"><Sel value={f.status} onChange={set("status")} options={PROJECT_STATUSES} /></F>
              <F label="Priority"><Sel value={f.priority} onChange={set("priority")} options={PRIORITIES} /></F>
              <F label="Project manager"><Sel testid="project-pm-select" value={f.pmId} onChange={set("pmId")} team={team} allowNone="Unassigned" /></F>
              <F label="Customer"><Sel value={f.customerId} onChange={set("customerId")} options={customers.map((c) => ({ value: c.id, label: c.name }))} /></F>
              <F label="Est start date"><Input type="date" value={f.startDate || ""} onChange={set("startDate")} /></F>
              <F label="Est end date"><Input type="date" value={f.dueDate || ""} onChange={set("dueDate")} /></F>
              <F label="Description" full><Textarea rows={3} value={f.description || ""} onChange={set("description")} /></F>
            </>
          )}
          {type === "task" && (
            <>
              <F label="Task name *" full><Input data-testid="task-name-input" value={f.title || ""} onChange={set("title")} autoFocus /></F>
              <F label="Project *" full><Sel testid="task-project-select" value={f.projectId} onChange={set("projectId")} options={pOpts} placeholder="Select a project" /></F>
              <F label="Assigned to"><Sel testid="task-assign-select" value={f.assigneeId} onChange={set("assigneeId")} team={team} allowNone="Unassigned" /></F>
              <F label="Project phase"><Sel value={f.phase} onChange={set("phase")} options={PHASES} /></F>
              <F label="Status"><Sel value={f.status} onChange={set("status")} options={TASK_STATUSES} /></F>
              <F label="Priority"><Sel value={f.priority} onChange={set("priority")} options={PRIORITIES} /></F>
              <F label="Start"><Input type="date" value={f.startDate || ""} onChange={set("startDate")} /></F>
              <F label="Projected finish"><Input type="date" value={f.dueDate || ""} onChange={set("dueDate")} /></F>
              <F label="Description" full><Textarea rows={3} value={f.description || ""} onChange={set("description")} /></F>
            </>
          )}
          {type === "customer" && (
            <>
              <F label="Customer name *" full><Input data-testid="customer-name-input" value={f.name || ""} onChange={set("name")} autoFocus /></F>
              <F label="Main contact"><Input value={f.contact || ""} onChange={set("contact")} /></F>
              <F label="Title"><Input value={f.title || ""} onChange={set("title")} /></F>
              <F label="Email"><Input type="email" value={f.email || ""} onChange={set("email")} /></F>
              <F label="Phone"><Input value={f.phone || ""} onChange={set("phone")} /></F>
              <F label="Street" full><Input value={f.street1 || ""} onChange={set("street1")} /></F>
              <F label="City"><Input value={f.city || ""} onChange={set("city")} /></F>
              <F label="Postcode"><Input value={f.postcode || ""} onChange={set("postcode")} /></F>
              <F label="Website" full><Input value={f.web || ""} onChange={set("web")} /></F>
              <F label="Notes" full><Textarea rows={2} value={f.notes || ""} onChange={set("notes")} /></F>
            </>
          )}
          {type === "team" && (
            <>
              <F label="Full name *" full><Input data-testid="team-name-input" value={f.name || ""} onChange={set("name")} autoFocus /></F>
              <F label="Title / role"><Input value={f.role || ""} onChange={set("role")} /></F>
              <F label="Organisation"><Sel testid="team-org-select" value={f.org || "EP Group"} onChange={set("org")} options={ORGS} /></F>
              <F label="Email" full><Input type="email" value={f.email || ""} onChange={set("email")} /></F>
              <F label="Phone"><Input value={f.phone || ""} onChange={set("phone")} /></F>
              <F label="Mobile"><Input value={f.mobile || ""} onChange={set("mobile")} /></F>
            </>
          )}
          {type === "document" && (
            <>
              <F label="Document name *" full><Input data-testid="document-name-input" value={f.name || ""} onChange={set("name")} autoFocus /></F>
              <F label="Category"><Sel value={f.category} onChange={set("category")} options={DOC_CATEGORIES} /></F>
              <F label="Project"><Sel value={f.projectId} onChange={set("projectId")} options={pOpts} allowNone="No project" /></F>
              <F label="Description" full><Textarea rows={2} value={f.description || ""} onChange={set("description")} /></F>
              <F label="Link (optional)" full><Input placeholder="https://" value={f.url || ""} onChange={set("url")} /></F>
              <F label="File" full>
                <label className="flex cursor-pointer items-center gap-3 rounded-lg border-2 border-dashed border-slate-200 p-4 text-sm text-slate-500 transition-colors hover:border-[#b8975a] hover:bg-[#fbf8f1]">
                  <Upload className="h-5 w-5 text-[#a08450]" />
                  <span className="truncate">{file ? file.name : "Click to choose a file (max 25MB)"}</span>
                  <input type="file" className="hidden" data-testid="document-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </label>
                {busy && file && <Progress value={pct} className="mt-2 h-1.5" />}
              </F>
            </>
          )}
          <DialogFooter className="col-span-2 mt-2">
            <button type="button" onClick={closeDialog} className="h-9 rounded-lg border border-slate-200 px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
            <button type="submit" disabled={busy} data-testid="dialog-submit" className="h-9 rounded-lg bg-[#00573f] px-4 text-sm font-semibold text-white hover:bg-[#004330] disabled:opacity-50">
              {busy ? "Saving..." : editing ? "Save changes" : "Create"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
